import { AttachmentPayload } from '@/shared/lib/helpers/payloadHelpers';
import { UKRAINE_PHONE_MASK_START } from '@/shared/ui';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { UserRequestDraft, UserService } from '@/api/types';

import { MissingPersonFormData, MissingPersonPayload } from '../model/types';

import { generateAddress } from './generateAddress';
import { collectAttachments, generateApplicationAttachment } from './payloadHelpers';

interface Props {
  withAttachments: boolean;
  requestId: UserRequestDraft['id'];
  formData: MissingPersonFormData;
  service: UserService;
  serviceFileManager?: ServiceFileManager;
}

export const generatePayload = async ({
  requestId,
  service,
  serviceFileManager,
  formData,
}: Props): Promise<MissingPersonPayload> => {
  const missing_person_registered_address = generateAddress(formData.missingPersonRegisteredAddress, {
    addressEqualValue: formData.missingPersonResidenceAddress.address_equal,
  });
  const missing_person_residence_address = generateAddress(
    formData.missingPersonResidenceAddress.address_equal
      ? formData.missingPersonRegisteredAddress
      : formData.missingPersonResidenceAddress,
  );
  const applicant_registered_address = generateAddress(formData.applicantRegisteredAddress, {
    addressEqualValue: formData.applicantResidenceAddress.address_equal,
  });
  const applicant_residence_address = generateAddress(
    formData.applicantResidenceAddress.address_equal
      ? formData.applicantRegisteredAddress
      : formData.applicantResidenceAddress,
  );

  const missing_person_disappearance_address = generateAddress(formData.missingPersonDisappearanceInfo);

  const basePayload: MissingPersonPayload = {
    requestId: requestId.toString(),
    service: {
      id: service.id,
      name: service.name,
      code: service.code,
    },
    applicant: {
      last_name: formData.applicantData.last_name,
      first_name: formData.applicantData.first_name,
      patronymic: formData.applicantData.patronymic,
      ancestral_connection_id: formData.applicantData.ancestral_connection?.id ?? '',
      ancestral_connection_name: formData.applicantData.ancestral_connection?.label ?? '',
      other_ancestral_connection: formData.applicantData.other_ancestral_connection,
      birthday: formData.applicantData.birthday,
      birth_place: formData.applicantData.birth_place,
      citizenship_id: formData.applicantData.citizenship?.id ?? '',
      citizenship_name: formData.applicantData.citizenship?.label ?? '',
      phones: formData.applicantData.phones?.filter((phone) => !!phone && phone !== UKRAINE_PHONE_MASK_START),
      rnokpp: formData.applicantData.rnokpp,
    },
    applicant_registered_address,
    applicant_residence_address,
    missing_person: {
      last_name: formData.missingPerson.last_name,
      first_name: formData.missingPerson.first_name,
      patronymic: formData.missingPerson.patronymic,
      rnokpp: formData.missingPerson.rnokpp,
      birthday: formData.missingPerson.birthday,
      sex: formData.missingPerson.sex?.id ?? '',
      birth_place: formData.missingPerson.birth_place,
      citizenship_id: formData.missingPerson.citizenship?.id ?? '',
      citizenship_name: formData.missingPerson.citizenship?.label ?? '',
      person_status: formData.missingPerson.person_status?.id ?? '',
      military_service_category: formData.missingPerson.military_service_category,
      military_service_type_id: formData.missingPerson.military_service_type?.id ?? '',
      military_service_type_name: formData.missingPerson.military_service_type?.label ?? '',
      military_unit: formData.missingPerson.military_unit,
      military_brigade: formData.missingPerson.military_brigade,
      military_formation: formData.missingPerson.military_formation,
      military_rank: formData.missingPerson.military_rank,
      military_call_sign: formData.missingPerson.military_call_sign,
      phones: formData.missingPerson.phones?.filter((phone) => !!phone && phone !== UKRAINE_PHONE_MASK_START),
      person_stature_id: formData.missingPerson.person_stature?.id ?? '',
      person_stature_name: formData.missingPerson.person_stature?.label ?? '',
      person_height: formData.missingPerson.person_height,
      person_hair_color_id: formData.missingPerson.person_hair_color?.id ?? '',
      person_hair_color_name: formData.missingPerson.person_hair_color?.label ?? '',
      person_eye_color_id: formData.missingPerson.person_eye_color?.id ?? '',
      person_eye_color_name: formData.missingPerson.person_eye_color?.label ?? '',
      personal_baldness: formData.missingPerson.personal_baldness?.id ?? '',
      personal_foot_size: formData.missingPerson.personal_foot_size,
      personal_blood_group_id: formData.missingPerson.personal_blood_group?.id ?? '',
      personal_blood_group_name: (formData.missingPerson.personal_blood_group?.label ?? '') as any,
      personal_scars: formData.missingPerson.personal_scars,
      personal_tattoo: formData.missingPerson.personal_tattoo,
      personal_stretching: formData.missingPerson.personal_stretching?.id ?? '',
      personal_fractures: formData.missingPerson.personal_fractures,
      personal_naevus: formData.missingPerson.personal_naevus,
      personal_birthmarks: formData.missingPerson.personal_birthmarks,
      personal_teeth_absence: formData.missingPerson.personal_teeth_absence,
      personal_crowns_presence: formData.missingPerson.personal_crowns_presence,
      personal_implants_presence: formData.missingPerson.personal_implants_presence,
      personal_other_info: formData.missingPerson.personal_other_info,
    },
    missing_person_registered_address,
    missing_person_residence_address,
    missing_person_disappearance_info: {
      ...missing_person_disappearance_address,
      disappearance_date: formData.missingPersonDisappearanceInfo.disappearance_date,
      disappearance_circumstances: formData.missingPersonDisappearanceInfo.disappearance_circumstances,
    },
    erdr: {
      number: formData.erdr.number,
      entry_date: formData.erdr.entry_date,
      dna_exists: formData.erdr.dna_exists ?? false,
      method: formData.erdr.method,
      dna_owners: formData.erdr.dna_owners.map((owner) => ({
        phones: owner.phones?.filter((phone) => !!phone && phone !== UKRAINE_PHONE_MASK_START),
        method: {
          id: owner?.method?.id,
          label: owner?.method?.label,
        },
        lastName: owner.lastName,
        name: owner.name,
        patronymic: owner.patronymic,
        birthday: owner.birthday,
        ancestralConnection: {
          id: owner?.ancestralConnection?.id,
          label: owner?.ancestralConnection?.label,
        },
      })),
      additional_info: formData.erdr.additional_info,
    },
    attachments: [
      ...collectAttachments(formData as any),
      await generateApplicationAttachment(serviceFileManager),
    ].filter((attach) => !!attach) as AttachmentPayload[],
  };

  return basePayload;
};
